import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import { q } from '@actual-app/core/shared/query';

import { AccionTexto } from '#components/mobile/inicio/comunes';
import { MOBILE_NAV_HEIGHT } from '#components/mobile/MobileNavTabs';
import { Hoja } from '#components/mobile/ui/Hoja';
import { Icono } from '#components/mobile/ui/Icono';
import { Tarjeta } from '#components/mobile/ui/Tarjeta';
import { color, espacio, num, texto } from '#components/mobile/ui/tokens';
import { Page } from '#components/Page';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useNavigate } from '#hooks/useNavigate';
import { useQuery } from '#hooks/useQuery';

import { CabeceraNomina } from './comun';
import {
  calendarioAno,
  clavePaga,
  pagaRecienCobrada,
  sueldoDeVerdad,
  totalAnual,
} from './nomina';
import { NominaSettings } from './NominaSettings';
import { nombrePaga, resumenNomina } from './textos';
import { useConfigNomina, usePlanesPagas } from './useNomina';

/**
 * «Calendario de nómina» (`/nomina`, maqueta 4): el año mes a mes con la
 * nómina y las pagas extra, lo ya cobrado marcado, si cada paga cobrada está
 * repartida y el «sueldo de verdad». Solo una paga que ya ha llegado se puede
 * abrir para repartirla: lo que aún no ha llegado no se planea. Sin nómina
 * configurada, la configuración.
 */
export function NominaPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = useConfigNomina();
  const { planes } = usePlanesPagas();
  const [editando, setEditando] = useState(location.state?.editar === true);
  const mesActual = monthUtils.currentMonth();
  const ano = Number(mesActual.slice(0, 4));
  const cobrado = useCobradoPorMes(ano);
  const recien = config
    ? pagaRecienCobrada(config, monthUtils.currentDay())
    : null;

  const configurada = config != null && config.neta > 0;

  return (
    <Page
      padding={0}
      header={
        <CabeceraNomina
          titulo={configurada ? t('Payroll calendar') : t('Your payroll')}
          derecha={
            configurada && (
              <AccionTexto onPress={() => setEditando(true)}>
                <Trans>Edit</Trans>
              </AccionTexto>
            )
          }
        />
      }
    >
      <View
        data-testid="nomina"
        style={{
          gap: espacio.tarjetas,
          padding: `0 ${espacio.margen}px`,
          paddingBottom: MOBILE_NAV_HEIGHT + espacio.margen,
          flexShrink: 0,
        }}
      >
        {!configurada || !config ? (
          <NominaSettings />
        ) : (
          <>
            <View
              style={{
                gap: 4,
                padding: '16px 18px',
                borderRadius: 20,
                backgroundColor: color.heroB,
              }}
            >
              <Text
                style={{ fontSize: 13, fontWeight: 700, color: color.heroFg }}
              >
                {resumenNomina(t, config)} · {ano}
              </Text>
              <Text
                style={{ ...texto.titulo, color: color.heroFg, ...num }}
                data-testid="nomina-sueldo-verdad"
              >
                <PrivacyFilter>
                  {t('{{amount}} a month, really', {
                    amount: format(sueldoDeVerdad(config), 'financial'),
                  })}
                </PrivacyFilter>
              </Text>
              <Text style={{ ...texto.secundario, color: color.heroFg }}>
                <PrivacyFilter>
                  {t(
                    '{{amount}} a year divided by 12 months. Spend as if you earned this.',
                    { amount: format(totalAnual(config), 'financial') },
                  )}
                </PrivacyFilter>
              </Text>
            </View>

            <Tarjeta relleno={0}>
              {calendarioAno(config, ano).map((fila, i) => {
                const pasado = fila.mes < mesActual;
                const esHoy = fila.mes === mesActual;
                const conExtra = fila.extras.length > 0;
                const llego = (cobrado.get(fila.mes) ?? 0) >= fila.nomina * 0.9;
                const extra = fila.extras[0];
                const clave = extra ? clavePaga(extra.paga.id, ano) : null;
                const plan = clave ? planes[clave] : undefined;
                // Se reparte la paga recién cobrada (los mismos 15 días del
                // aviso de Inicio); lo que viene no se planea y lo pasado sin
                // repartir no pide nada.
                const porRepartir =
                  clave != null &&
                  recien?.clave === clave &&
                  (plan == null || plan.estado === 'pendiente');
                const conEstado = porRepartir || plan?.estado === 'repartida';
                const contenido = (
                  <>
                    <Text
                      style={{
                        width: 44,
                        fontSize: 14,
                        fontWeight: conExtra || esHoy ? 800 : 500,
                        color: conExtra
                          ? color.accent
                          : esHoy
                            ? color.fg
                            : color.fg3,
                      }}
                    >
                      {monthUtils.format(fila.mes, 'MMM', locale)}
                    </Text>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        style={{
                          fontSize: 14,
                          fontWeight: conExtra ? 700 : 500,
                          color: pasado || esHoy ? color.fg : color.fg3,
                        }}
                      >
                        {conExtra
                          ? t('Pay + {{name}}', {
                              name: nombrePaga(t, extra.paga, locale),
                            })
                          : t('Monthly pay')}
                      </Text>
                      {conEstado && (
                        <Text
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: porRepartir ? color.warn : color.fg2,
                          }}
                        >
                          {porRepartir ? t('To share out') : t('Shared out')}
                        </Text>
                      )}
                    </View>
                    <Text
                      style={{
                        ...num,
                        fontSize: 14,
                        fontWeight: conExtra ? 800 : 700,
                        color: pasado ? color.fg : color.fg3,
                      }}
                    >
                      <PrivacyFilter>
                        {format(fila.total, 'financial')}
                      </PrivacyFilter>
                    </Text>
                    <View style={{ width: 18, alignItems: 'center' }}>
                      {pasado && llego ? (
                        <Icono
                          nombre="check"
                          size={16}
                          style={{ color: color.ok }}
                        />
                      ) : porRepartir ? (
                        <Icono
                          nombre="cr"
                          size={16}
                          style={{ color: color.fg3 }}
                        />
                      ) : null}
                    </View>
                  </>
                );
                const estilo = {
                  flexDirection: 'row' as const,
                  alignItems: 'center',
                  gap: 10,
                  minHeight: conExtra ? 56 : 44,
                  padding: '0 14px',
                  borderTop: i === 0 ? 0 : `1px solid ${color.line}`,
                  backgroundColor: conExtra ? color.accentSoft : 'transparent',
                };
                return porRepartir && clave ? (
                  <Button
                    key={fila.mes}
                    variant="bare"
                    data-testid={`nomina-mes-${fila.mes}`}
                    aria-label={t('Share out your {{name}}', {
                      name: nombrePaga(t, extra.paga, locale),
                    })}
                    onPress={() =>
                      void navigate(`/pagas/${encodeURIComponent(clave)}`)
                    }
                    style={{
                      ...estilo,
                      justifyContent: 'flex-start',
                      borderRadius: 0,
                      textAlign: 'left',
                    }}
                  >
                    {contenido}
                  </Button>
                ) : (
                  <View
                    key={fila.mes}
                    data-testid={`nomina-mes-${fila.mes}`}
                    style={estilo}
                  >
                    {contenido}
                  </View>
                );
              })}
            </Tarjeta>
            <Text style={{ ...texto.pequeno, color: color.fg3 }}>
              <Trans>
                The tick means your pay for that month is already in your
                transactions; the rest comes from your settings.
              </Trans>
            </Text>
          </>
        )}
      </View>
      {editando && (
        <Hoja
          aria-label={t('Your payroll')}
          data-testid="nomina-hoja-editar"
          onCerrar={() => setEditando(false)}
        >
          <View style={{ gap: 12, paddingBottom: 8 }}>
            <Text style={{ ...texto.titulo, color: color.fg }}>
              <Trans>Your payroll</Trans>
            </Text>
            <NominaSettings />
          </View>
        </Hoja>
      )}
    </Page>
  );
}

/** Ingresos (sin traspasos) de cada mes del año, para marcar ✓. */
function useCobradoPorMes(ano: number) {
  const { data } = useQuery<{ date: string; amount: number }>(
    () =>
      q('transactions')
        .filter({
          amount: { $gt: 0 },
          transfer_id: null,
          'account.offbudget': false,
          date: { $gte: `${ano}-01-01`, $lte: `${ano}-12-31` },
        })
        .select(['date', 'amount']),
    [ano],
  );
  return useMemo(() => {
    const porMes = new Map<string, number>();
    for (const fila of data ?? []) {
      const mes = fila.date.slice(0, 7);
      porMes.set(mes, (porMes.get(mes) ?? 0) + fila.amount);
    }
    return porMes;
  }, [data]);
}
